import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { UnresolvedDepartment, User } from '@bliks-io/contracts';
import { isForbiddenError } from '@/api/errors';
import { useResolveDepartmentManager } from '@/api/mutations/useResolveDepartmentManager';
import { useInitializeUnresolvedDepartments } from '@/api/queries/useInitializeUnresolvedDepartments';
import type { WizardStepId } from './wizardSteps';
import type { ResolvedManagerEmailUpdate } from './utils/resolvedManagerEmails';
import { effectiveManagerId } from './utils/departmentManager';

interface UseResolutionSubmitArgs {
  managerAssignments: Map<string, string>;
  setManagerAssignments: React.Dispatch<React.SetStateAction<Map<string, string>>>;
  emailToUser: Map<string, User>;
  goTo: (step: WizardStepId) => void;
  /** Syncs a successful manual resolution's manager email into the departments draft/baseline. */
  onDepartmentsResolved: (updates: ResolvedManagerEmailUpdate[]) => void;
}

/**
 * Weiter handler of the Manager-auflösen sub-step: resolves the still-open
 * departments (any unresolved department is resolvable independently) and
 * moves on once none are left.
 */
export function useResolutionSubmit({
  managerAssignments,
  setManagerAssignments,
  emailToUser,
  goTo,
  onDepartmentsResolved
}: UseResolutionSubmitArgs) {
  const { t } = useTranslation();
  const [isResolving, setIsResolving] = useState(false);
  const resolveDepartmentManager = useResolveDepartmentManager();
  const { data: unresolvedDepartments } = useInitializeUnresolvedDepartments();

  // Reverse of emailToUser — resolutions carry the chosen manager's id, but
  // syncing the draft's managerEmail column needs that manager's email.
  const userById = useMemo(() => {
    const map = new Map<string, User>();
    emailToUser.forEach((user) => map.set(user._id, user));
    return map;
  }, [emailToUser]);

  const resolveAll = useCallback(
    async (resolutions: Array<{ dept: UnresolvedDepartment; managerId: string }>) => {
      // Independent by design (ADR 0012: no parent-ordering) — fire in parallel.
      const results = await Promise.allSettled(
        resolutions.map(({ dept, managerId }) =>
          resolveDepartmentManager.mutateAsync({ id: dept._id, data: { managerId } })
        )
      );
      const succeeded = resolutions.filter((_, index) => results[index].status === 'fulfilled');
      const failureReasons = results.flatMap((result) =>
        result.status === 'rejected' ? [result.reason as unknown] : []
      );
      return { succeeded, failureReasons };
    },
    [resolveDepartmentManager]
  );

  const submitResolutions = useCallback(async () => {
    const open = unresolvedDepartments ?? [];
    if (open.length === 0) {
      goTo('functions');
      return;
    }

    const resolutions = open
      .map((dept) => {
        const managerId = effectiveManagerId(dept, managerAssignments, emailToUser);
        return managerId ? { dept, managerId } : null;
      })
      .filter((r): r is { dept: UnresolvedDepartment; managerId: string } => r !== null);

    if (resolutions.length === 0) {
      toast.error(t('bulkOnboarding.resolution.toast.unresolvedDepartments'));
      return;
    }

    setIsResolving(true);
    try {
      const { succeeded, failureReasons } = await resolveAll(resolutions);

      if (failureReasons.length === 0 && succeeded.length > 0) {
        toast.success(
          t('bulkOnboarding.resolution.toast.batchSuccess', { count: succeeded.length })
        );
      } else if (succeeded.length > 0) {
        toast.warning(
          t('bulkOnboarding.resolution.toast.partialSuccess', {
            success: succeeded.length,
            failed: failureReasons.length
          })
        );
      } else if (!failureReasons.every(isForbiddenError)) {
        toast.error(t('bulkOnboarding.resolution.toast.batchFailed'));
      }

      if (succeeded.length > 0) {
        const resolvedIds = new Set(succeeded.map(({ dept }) => dept._id));
        setManagerAssignments((prev) => {
          const next = new Map(prev);
          resolvedIds.forEach((id) => next.delete(id));
          return next;
        });

        // The invalidated departments/unresolved-departments queries refetch
        // the resolved manager, but the DRAFT rows (client state) keep their
        // stale managerEmail until then — sync it now so navigating back to
        // the departments step shows it immediately instead of only after a
        // refresh.
        const resolvedUpdates = succeeded
          .map(({ dept, managerId }) => {
            const manager = userById.get(managerId);
            return manager ? { entityId: dept._id, managerEmail: manager.email } : null;
          })
          .filter((update): update is ResolvedManagerEmailUpdate => update !== null);
        onDepartmentsResolved(resolvedUpdates);
      }

      // No navigation on full resolution: the awaited invalidations refetch
      // the unresolved list, the post-resolve locks release, and the render
      // derivation falls back to the URL step the user was detoured from
      // (or to deriveResumeStep without one). An explicit goTo('functions')
      // would strand a user who was headed elsewhere.
    } finally {
      setIsResolving(false);
    }
  }, [
    managerAssignments,
    emailToUser,
    goTo,
    onDepartmentsResolved,
    resolveAll,
    setManagerAssignments,
    t,
    unresolvedDepartments,
    userById
  ]);

  return {
    submitResolutions,
    isResolutionPending: isResolving || resolveDepartmentManager.isPending
  };
}
